import { oc } from "@orpc/contract";
import { z } from "zod";
import { FieldProvenanceSchema } from "@erp/shared-types";
import { DiscountValidationResultSchema } from "./sales-order-drafts.contract";

/**
 * Relational draft-sales-order editing contracts (workflow-native approval flow).
 *
 * Reads + per-line/header mutations over `ar_draft_sales_orders` +
 * `ar_draft_sales_order_line_items`. Line mutations are keyed by the stable
 * line-item `id` (not array index), the relational replacement for the staged
 * flow's PATCH-whole-JSONB pattern.
 */

const UUIDSchema = z.string().uuid();

// DECIMAL columns come back from Sequelize as strings; accept either on the wire.
const NumericSchema = z.union([z.string(), z.number()]);

const DraftLineItemSchema = z
  .object({
    id: UUIDSchema,
    lineNumber: z.number().int(),
    customerIdentifier: z.string().nullable().optional(),
    customerDescription: z.string().nullable().optional(),
    quantity: NumericSchema.nullable().optional(),
    unitPrice: NumericSchema.nullable().optional(),
    amount: NumericSchema.nullable().optional(),
    unit: z.string().nullable().optional(),
    warehouse: z.string().nullable().optional(),
    selectedProductVariantId: UUIDSchema.nullable().optional(),
    selectedCustomerProductId: UUIDSchema.nullable().optional(),
    inventoryStatus: z.string().nullable().optional(),
    // Stamped facts the approval conditions read.
    listPrice: NumericSchema.nullable().optional(),
    listPriceMismatch: z.boolean().nullable().optional(),
    approvalIssue: z.unknown().nullable().optional(),
    customerSpecificDiscountVerification: z.unknown().nullable().optional(),
    // The sparse long tail (sku, itemName, origin, charges, EDI loops, …).
    additionalData: z.record(z.string(), z.unknown()).nullable().optional(),
  })
  // No passthrough: Zod strips the model's extra columns at the wire boundary,
  // and the trimmed type lets the handler return `row.toJSON()` via width subtyping.
  .strip();

const DraftSalesOrderSchema = z
  .object({
    id: UUIDSchema,
    organizationId: UUIDSchema,
    customerId: UUIDSchema.nullable().optional(),
    suggestedCustomerId: UUIDSchema.nullable().optional(),
    purchaseOrderNumber: z.string().nullable().optional(),
    status: z.string(),
    orderDate: z.string().nullable().optional(),
    shipDate: z.string().nullable().optional(),
    currency: z.string().nullable().optional(),
    salesOrderId: UUIDSchema.nullable().optional(),
    lineItems: z.array(DraftLineItemSchema),
  })
  .strip();

const DraftLineItemPatchSchema = z.object({
  customerIdentifier: z.string().nullable().optional(),
  customerDescription: z.string().nullable().optional(),
  quantity: NumericSchema.nullable().optional(),
  unitPrice: NumericSchema.nullable().optional(),
  amount: NumericSchema.nullable().optional(),
  unit: z.string().nullable().optional(),
  warehouse: z.string().nullable().optional(),
  selectedProductVariantId: UUIDSchema.nullable().optional(),
  selectedCustomerProductId: UUIDSchema.nullable().optional(),
});

const DraftHeaderPatchSchema = z.object({
  // Direct draft-row columns.
  customerId: UUIDSchema.nullable().optional(),
  purchaseOrderNumber: z.string().nullable().optional(),
  orderDate: z.string().nullable().optional(),
  shipDate: z.string().nullable().optional(),
  currency: z.string().nullable().optional(),
  shipToLocationId: UUIDSchema.nullable().optional(),
  billToLocationId: UUIDSchema.nullable().optional(),
  // Resolver-driven header fields, stored as user overrides once in draft form
  // (computed only at ingest; edits never re-run their resolver — see v2's
  // updateSalesOrderDraftField). shippingMethod additionally re-derives freight
  // terms, which the resolver dependency handles.
  shippingMethod: z.string().nullable().optional(),
  freightTerms: z.string().nullable().optional(),
  freightCarrierInstructions: z.string().nullable().optional(),
  // Request date is resolver-computed (request_date); editing it stores a user
  // override (its resolver never re-runs in draft form), like the fields above.
  requestDate: z.string().nullable().optional(),
  // Order type is resolver-computed as an { id, name } object; edits store the
  // picked option verbatim as a user override (mirrors v2's
  // updateSalesOrderDraftField, which accepts the full object).
  orderType: z
    .object({ id: z.number(), name: z.string() })
    .nullable()
    .optional(),
  // Warehouse (ship-from org code) — resolver-computed string, user-overridable.
  warehouse: z.string().nullable().optional(),
});

const SuccessSchema = z.object({ success: z.literal(true) });

/** Source document descriptor for the provenance viewer (signed URL + kind). */
const ProvenanceDocumentSchema = z.object({
  id: UUIDSchema,
  originalFileName: z.string(),
  mimeType: z.string().nullable(),
  url: z.string(),
  kind: z.enum(["pdf", "image", "xlsx", "emailBody"]),
});

/** Field-level source provenance for a draft, grouped for the review page. */
const DraftProvenanceResponseSchema = z.object({
  documents: z.array(ProvenanceDocumentSchema),
  headerFields: z.array(FieldProvenanceSchema),
  lineItems: z.array(
    z.object({
      lineItemId: UUIDSchema,
      lineNumber: z.number().int(),
      fields: z.array(FieldProvenanceSchema),
    }),
  ),
  unmappedBlocks: z.array(FieldProvenanceSchema),
  totalsReconciliation: z.object({
    extractedTotal: z.number().nullable(),
    computedLineItemsTotal: z.number(),
    matches: z.boolean(),
  }),
});

/** A single node in the workflow pipeline projection (review-page stepper). */
const WorkflowStateNodeSchema = z.object({
  id: z.string(),
  label: z.string(),
  status: z.enum(["done", "active", "wait"]),
  sub: z.string().nullable(),
  group: z.literal("par").nullable(),
  kind: z.literal("approval").nullable(),
  role: z.string().nullable(),
  assignee: z.string().nullable(),
  decision: z
    .enum(["approved", "rejected", "escalated", "timed_out", "pending"])
    .nullable(),
  /** Latest StepRun id for the step — the `submitWorkflowApproval` handle. */
  stepRunId: UUIDSchema.nullable(),
  /** The requesting user may decide this awaiting approval gate right now. */
  canDecide: z.boolean(),
});

/**
 * Rolled-up workflow state for the lifecycle stepper + pipeline. `hasWorkflow`
 * is false for drafts with no run yet (the stepper still renders `stage`).
 */
const WorkflowStateSchema = z.object({
  // Keep in sync with `OrderLifecycleStage` in `@erp/shared-types`.
  stage: z.enum([
    "draft",
    "creating_discounts",
    "approving",
    "ready",
    "created",
    "rejected",
    "superseded",
  ]),
  hasWorkflow: z.boolean(),
  nodes: z.array(WorkflowStateNodeSchema),
  approvalsTotal: z.int().nonnegative(),
  approvalsDone: z.int().nonnegative(),
  rejected: z.boolean(),
  rejectionComment: z.string().nullable(),
  salesOrderId: UUIDSchema.nullable(),
  /** Last failed ERP submission attempt, until a retry succeeds. */
  submissionError: z.object({ message: z.string(), at: z.string() }).nullable(),
});

/** A single per-draft activity entry (spec §6 Activity rail). */
const ActivityEntrySchema = z.object({
  type: z.string(),
  at: z.string(),
  actorUserId: z.string().nullable(),
  actorName: z.string().nullable(),
  message: z.string().nullable(),
  lineItemId: z.string().nullable(),
  valuesChanged: z.record(z.string(), z.unknown()).nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
});

/** A single extraction-check mismatch (spec §5) — common shape across detectors. */
const MismatchSchema = z.object({
  key: z.string(),
  detectionMethod: z.enum([
    "totals_reconciliation",
    "coverage_sweep",
    "email_cross_reference",
    "catalog_validation",
  ]),
  status: z.enum(["open", "added", "pending", "dismissed"]),
  severity: z.enum(["warning", "info"]),
  title: z.string(),
  detail: z.string(),
  evidence: z.array(
    z.object({
      source: z.string(),
      label: z.string(),
      value: z.string(),
    }),
  ),
  resolvedAt: z.string().nullable(),
  resolvedByUserId: z.string().nullable(),
  resolutionReason: z.string().nullable(),
});

/**
 * Result of an ERP inventory-availability check for one line (mirrors
 * `InventoryValidationResult` in @erp/shared-types).
 */
const InventoryCheckResultSchema = z.object({
  status: z.string(),
  requestedQuantity: z.number(),
  availableQuantity: z.number().nullable(),
  onhandQuantity: z.number().nullable(),
  reservedQuantity: z.number().nullable(),
  organizationCode: z.string().nullable(),
  itemNumber: z.string().nullable(),
  errorMessage: z.string().optional(),
  itemStatus: z.string().optional(),
});

/** Source-email descriptor for the review page Emails tab. */
const SourceEmailThreadSchema = z.object({
  /** The draft's source type (e.g. "email", "edi", "direct_entry"). */
  source: z.string().nullable(),
  /** Graph conversation/thread id — feeds the email-thread viewer. */
  conversationId: z.string().nullable(),
  /** The source inbound email id (for highlighting in the thread). */
  sourceEmailId: UUIDSchema.nullable(),
  /** Sender of the source email (seeds the reply composer's To). */
  fromAddress: z.string().nullable(),
  /** Subject of the source email. */
  subject: z.string().nullable(),
});

export const draftSalesOrdersContracts = {
  get: oc
    .input(z.object({ id: UUIDSchema }))
    .output(z.object({ draftSalesOrder: DraftSalesOrderSchema }))
    .route({ method: "GET" }),
  // Workflow-state projection for the review-page lifecycle stepper + pipeline.
  getWorkflowState: oc
    .input(z.object({ draftSalesOrderId: UUIDSchema }))
    .output(WorkflowStateSchema)
    .route({ method: "GET" }),
  // Per-line discount validation for the v3 review page (reuses the relational
  // draft validator). Keyed by line-item id. Powers the per-line DiscountBadge
  // popover + the bulk create/approve flows.
  validateDiscounts: oc
    .input(z.object({ draftSalesOrderId: UUIDSchema }))
    .output(
      z.object({
        results: z.record(z.string(), DiscountValidationResultSchema),
      }),
    )
    .route({ method: "GET" }),
  // Extraction-check / mismatch detection (spec §5). A stateless second pass;
  // resolution state (dismissed / added / pending) persists per mismatch key.
  getMismatches: oc
    .input(z.object({ draftSalesOrderId: UUIDSchema }))
    .output(z.object({ mismatches: z.array(MismatchSchema) }))
    .route({ method: "GET" }),
  resolveMismatch: oc
    .input(
      z.object({
        draftSalesOrderId: UUIDSchema,
        key: z.string().min(1),
        status: z.enum(["open", "added", "pending", "dismissed"]),
        reason: z.string().max(2000).nullable().optional(),
      }),
    )
    .output(SuccessSchema)
    .route({ method: "POST" }),
  // Retry a failed ERP submission: re-emits DRAFT_SALES_ORDER_READY so the
  // approval chain re-runs (gates auto-pass from recorded decisions) and the
  // Submit step attempts the submission again.
  retryErpSubmission: oc
    .input(z.object({ draftSalesOrderId: UUIDSchema }))
    .output(SuccessSchema)
    .route({ method: "POST" }),
  // Admin escape hatch: replay the approval-chain workflow for the draft
  // (facts recompute, gates route on recorded decisions, submit re-attempts).
  reinitiateWorkflow: oc
    .input(z.object({ draftSalesOrderId: UUIDSchema }))
    .output(SuccessSchema)
    .route({ method: "POST" }),
  // Per-draft activity timeline (spec §6) — the action-activity log, newest first.
  getActivity: oc
    .input(z.object({ draftSalesOrderId: UUIDSchema }))
    .output(z.object({ entries: z.array(ActivityEntrySchema) }))
    .route({ method: "GET" }),
  updateLineItem: oc
    .input(
      z.object({
        draftSalesOrderId: UUIDSchema,
        lineItemId: UUIDSchema,
        patch: DraftLineItemPatchSchema,
      }),
    )
    .output(SuccessSchema)
    .route({ method: "PATCH" }),
  // Match a line to a product variant (the review-page "Match" column). Beyond
  // updateLineItem, this also upserts the CustomerProduct mapping (customer
  // freeform learning, v2 parity) so future orders from the customer
  // auto-match, then writes selectedProductVariantId/-CustomerProductId through
  // the shared line-edit path (discount-stack removal, fact re-stamp, approval
  // invalidation). Learning is skipped when the draft has no customer.
  matchLineItem: oc
    .input(
      z.object({
        draftSalesOrderId: UUIDSchema,
        lineItemId: UUIDSchema,
        productVariantId: UUIDSchema,
      }),
    )
    .output(SuccessSchema)
    .route({ method: "POST" }),
  // Append a manually-added line item (product search + qty + unit price). The
  // backend stamps the next line number; list-price facts are (re)stamped by the
  // approval recheck that the add triggers.
  addLineItem: oc
    .input(
      z.object({
        draftSalesOrderId: UUIDSchema,
        selectedProductVariantId: UUIDSchema,
        quantity: NumericSchema,
        unitPrice: NumericSchema,
        unit: z.string().nullable().optional(),
        customerIdentifier: z.string().nullable().optional(),
        customerDescription: z.string().nullable().optional(),
      }),
    )
    .output(SuccessSchema)
    .route({ method: "POST" }),
  deleteLineItem: oc
    .input(
      z.object({
        draftSalesOrderId: UUIDSchema,
        lineItemId: UUIDSchema,
      }),
    )
    .output(SuccessSchema)
    .route({ method: "DELETE" }),
  updateHeader: oc
    .input(
      z.object({
        draftSalesOrderId: UUIDSchema,
        patch: DraftHeaderPatchSchema,
      }),
    )
    .output(SuccessSchema)
    .route({ method: "PATCH" }),
  // Field-level source provenance (Extend bounding boxes) for the review page:
  // source documents + per-header / per-line / unmapped citations + a totals
  // reconciliation. Read-only.
  getProvenance: oc
    .input(z.object({ draftSalesOrderId: UUIDSchema }))
    .output(DraftProvenanceResponseSchema)
    .route({ method: "GET" }),
  // Source-email descriptor for the review page's Emails tab. Resolves the
  // draft (org-scoped) → its source InboundEmail, returning the conversationId
  // the existing email-thread viewer fetches the full thread with, plus the
  // sender + subject to seed a reply. conversationId is null for non-email
  // sources (EDI / direct entry) or emails without a Graph thread.
  getSourceEmailThread: oc
    .input(z.object({ draftSalesOrderId: UUIDSchema }))
    .output(SourceEmailThreadSchema)
    .route({ method: "GET" }),
  // Inventory-availability check for the draft's lines (v3 counterpart of the
  // legacy staged-PO validate-inventory endpoint). POST but read-mostly: it
  // queries the org's ERP and persists each line's inventoryStatus. Results
  // are keyed by productVariantId (and customerProductId where matched via one).
  validateInventory: oc
    .input(z.object({ draftSalesOrderId: UUIDSchema }))
    .output(
      z.object({ results: z.record(z.string(), InventoryCheckResultSchema) }),
    )
    .route({ method: "POST" }),
} as const;

export type DraftInventoryCheckResult = z.infer<
  typeof InventoryCheckResultSchema
>;
export type DraftSalesOrderDto = z.infer<typeof DraftSalesOrderSchema>;
export type DraftLineItemDto = z.infer<typeof DraftLineItemSchema>;
export type DraftSalesOrderWorkflowState = z.infer<typeof WorkflowStateSchema>;
export type DraftMismatch = z.infer<typeof MismatchSchema>;
export type DraftActivityEntry = z.infer<typeof ActivityEntrySchema>;
export type DraftSalesOrderWorkflowNode = z.infer<
  typeof WorkflowStateNodeSchema
>;
export type DraftLineItemPatch = z.infer<typeof DraftLineItemPatchSchema>;
export type DraftHeaderPatch = z.infer<typeof DraftHeaderPatchSchema>;
export type DraftProvenanceResponse = z.infer<
  typeof DraftProvenanceResponseSchema
>;
export type SourceEmailThread = z.infer<typeof SourceEmailThreadSchema>;
